import { CircleAlert, CircleCheck, Smartphone } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SettingsPageShell } from "../../Components/SettingsList";
import {
  disconnectHealthProvider,
  updateHealthPermissions,
} from "../../Services/connectedHealth";
import { showErrorToast, showSuccessToast } from "../../Utils/toast";
import ConfirmDialog from "./ConfirmDialog";
import LoadState from "./LoadState";
import { CategoryIcon, ProviderIcon } from "./icons";
import {
  CATEGORY_STATE,
  STATUS,
  categoryState,
  categoryStateLabel,
  connectionFor,
  errorMessage,
  grantsFor,
  historyAccessLabel,
  isActiveConnection,
  isComingSoon,
  statusLabel,
  syncedAtLabel,
} from "./model";
import { useConnectedHealth, useStandaloneHeader } from "./useConnectedHealth";
import "./style.css";

const PHONE_NOTE = "Connect and sync from the Aris app on your phone.";

function CategoryToggle({ category, state, disabled, onToggle }) {
  const on = state === CATEGORY_STATE.ON;
  const stateText = categoryStateLabel(state);
  const labelId = `ch-cat-${category.key}`;
  return (
    <li className="ch-category">
      <CategoryIcon categoryKey={category.key} />
      <span className="ch-category__text">
        <span className="ch-category__title" id={labelId}>
          {category.title}
        </span>
        {category.description && (
          <span className="ch-category__desc">{category.description}</span>
        )}
      </span>
      <span
        id={`${labelId}-state`}
        className={`ch-category__state ch-category__state--${state}`}
      >
        {stateText}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-labelledby={labelId}
        aria-describedby={`${labelId}-state`}
        disabled={disabled}
        className={`aris-settings-toggle${on ? " is-on" : ""}`}
        onClick={() => onToggle(category, !on)}
      >
        <span className="aris-settings-toggle__knob" />
      </button>
    </li>
  );
}

function CategoryList({ categories }) {
  return (
    <ul className="ch-card ch-category-list">
      {categories.map((c) => (
        <li key={c.key} className="ch-category">
          <CategoryIcon categoryKey={c.key} />
          <span className="ch-category__text">
            <span className="ch-category__title">{c.title}</span>
            {c.description && <span className="ch-category__desc">{c.description}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

function PhoneNote({ children = PHONE_NOTE }) {
  return (
    <div className="ch-note" role="note">
      <span className="ch-note__icon" aria-hidden="true">
        <Smartphone size={20} strokeWidth={1.5} />
      </span>
      <p>{children}</p>
    </div>
  );
}

function NotConnectedView({ provider, copy }) {
  const about = provider.copy || {};
  const categories = provider.categories || [];
  return (
    <>
      <PhoneNote />
      <section className="ch-card ch-about" aria-labelledby="ch-about-title">
        <h3 className="ch-section-title" id="ch-about-title">
          {about.about_title || `About ${provider.display_name}`}
        </h3>
        {about.about && <p className="ch-about__text">{about.about}</p>}
        {categories.length > 0 && <CategoryList categories={categories} />}
        {copy.history_note && <p className="ch-about__note">{copy.history_note}</p>}
      </section>
    </>
  );
}

function ConnectedView({ provider, connection, busy, onToggle, onDisconnect }) {
  const categories = provider.categories || [];
  const attention = connection.status === STATUS.NEEDS_ATTENTION;
  const StatusIcon = attention ? CircleAlert : CircleCheck;
  return (
    <>
      {attention && (
        <PhoneNote>
          This connection needs attention. Open the Aris app on your phone to reconnect.
        </PhoneNote>
      )}
      <dl className="ch-card ch-facts">
        <div className="ch-fact">
          <dt>Status</dt>
          <dd className={attention ? "ch-fact--warn" : "ch-fact--ok"}>
            <StatusIcon aria-hidden="true" size={14} />
            {statusLabel(connection)}
          </dd>
        </div>
        <div className="ch-fact">
          <dt>Last Synced</dt>
          <dd>{syncedAtLabel(connection.last_successful_sync_at)}</dd>
        </div>
        <div className="ch-fact">
          <dt>History Access</dt>
          <dd>{historyAccessLabel(connection.historical_permission_state)}</dd>
        </div>
      </dl>

      {categories.length > 0 && (
        <section aria-labelledby="ch-categories-title">
          <h3 className="ch-section-title" id="ch-categories-title">
            Data Categories
          </h3>
          <ul className="ch-card ch-category-list">
            {categories.map((c) => (
              <CategoryToggle
                key={c.key}
                category={c}
                state={categoryState(c, connection.authorized_families)}
                disabled={busy}
                onToggle={onToggle}
              />
            ))}
          </ul>
        </section>
      )}

      <p className="ch-muted">Sync happens from the Aris app on your phone.</p>

      <button
        type="button"
        className="ch-btn ch-btn--link-danger"
        onClick={onDisconnect}
        disabled={busy}
      >
        Disconnect
      </button>
    </>
  );
}

/**
 * `/connected-health/:provider` — a provider's manage screen (Figma
 * 11128:25422 not connected, 11128:25522 connected). There is no Connect or
 * Sync Now on web; the member can turn categories on/off and disconnect.
 */
function ProviderDetail() {
  const navigate = useNavigate();
  const { provider: providerKey } = useParams();
  useStandaloneHeader();
  const { status, data, reload } = useConnectedHealth();
  const [busy, setBusy] = useState(false);
  // { kind: "revoke", category } | { kind: "disconnect" } | null
  const [confirm, setConfirm] = useState(null);

  const provider = (data?.providers || []).find((p) => p.provider === providerKey);
  const connection = connectionFor(data?.connections, providerKey);
  const title = provider?.display_name || "Connected Health";
  const back = () => navigate("/connected-health");

  const run = async (call, success, failure) => {
    setBusy(true);
    try {
      await call();
      showSuccessToast(success);
    } catch (error) {
      showErrorToast(errorMessage(error, failure));
    } finally {
      await reload();
      setBusy(false);
      setConfirm(null);
    }
  };

  const setCategory = (category, turnOn) =>
    run(
      () => updateHealthPermissions(providerKey, grantsFor(category, turnOn)),
      `${category.title} turned ${turnOn ? "on" : "off"}`,
      `Couldn't turn ${category.title} ${turnOn ? "on" : "off"}. Please try again.`
    );

  const onToggle = (category, turnOn) => {
    if (busy) return;
    if (turnOn) setCategory(category, true);
    else setConfirm({ kind: "revoke", category });
  };

  const disconnect = () =>
    run(
      () => disconnectHealthProvider(providerKey),
      `${title} disconnected`,
      `Couldn't disconnect ${title}. Please try again.`
    );

  let body;
  if (!data) {
    body = <LoadState status={status} onRetry={reload} />;
  } else if (!provider) {
    body = (
      <p className="ch-state" role="alert">
        We couldn't find that health provider.
      </p>
    );
  } else if (isComingSoon(provider)) {
    body = (
      <p className="ch-state">
        {provider.display_name} is coming soon. You'll be able to connect it from the Aris app.
      </p>
    );
  } else {
    const active = isActiveConnection(connection);
    body = (
      <>
        <header className="ch-hero">
          <ProviderIcon provider={provider.provider} />
          <h2 className="ch-hero__title">{provider.display_name}</h2>
          {!active && <p className="ch-hero__status">Not connected</p>}
        </header>
        {active ? (
          <ConnectedView
            provider={provider}
            connection={connection}
            busy={busy}
            onToggle={onToggle}
            onDisconnect={() => setConfirm({ kind: "disconnect" })}
          />
        ) : (
          <NotConnectedView provider={provider} copy={data.copy || {}} />
        )}
      </>
    );
  }

  const revoking = confirm?.kind === "revoke" ? confirm.category : null;

  return (
    <SettingsPageShell title={title} onBack={back}>
      <div className="ch-page">{body}</div>
      <ConfirmDialog
        open={!!revoking}
        title={revoking ? `Turn off ${revoking.title}?` : ""}
        description={
          revoking
            ? `Aris will stop using your ${revoking.title.toLowerCase()} data from ${title}, and the ${revoking.title.toLowerCase()} data already shared will be deleted.`
            : ""
        }
        confirmLabel="Turn off"
        destructive
        busy={busy}
        onConfirm={() => revoking && setCategory(revoking, false)}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm?.kind === "disconnect"}
        title={`Disconnect ${title}?`}
        description={`Aris will stop syncing from ${title} and delete the health data it shared. You can reconnect from the Aris app on your phone.`}
        confirmLabel="Disconnect"
        destructive
        busy={busy}
        onConfirm={disconnect}
        onCancel={() => setConfirm(null)}
      />
    </SettingsPageShell>
  );
}

export default ProviderDetail;
